import { useCallback, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  ChevronDown,
  Info,
  Lightbulb,
  Loader2,
  Percent,
  Wallet,
} from 'lucide-react'
import apiClient from '@/api/client'
import { toastHelpers } from '@/lib/toast-helpers'
import { useCurrency } from '@/contexts/CurrencyContext'
import { currencyInputPrefix, formatMoney } from '@/lib/formatMoney'
import { cn } from '@/lib/utils'
import { ProductImagePlaceholder } from '@/components/menu/ProductImagePlaceholder'
import type { Product, Category, ProjectedCostBasis } from '@/types'

const COSTING_GUIDE_EXPANDED_KEY = 'pos_menu_costing_guide_expanded'

function readCostingGuideExpanded(): boolean {
  try {
    const raw = localStorage.getItem(COSTING_GUIDE_EXPANDED_KEY)
    if (raw === null) return true
    return raw === 'true'
  } catch {
    return true
  }
}

interface RowDraft {
  basis: ProjectedCostBasis
  cost: string
  percent: string
  /** Tracks dirty state so we can decide which mutation to send. */
  dirty: boolean
}

function productHasCost(p: Product): boolean {
  return p.projected_cost != null
}

function deriveDraft(p: Product): RowDraft {
  return {
    basis: p.projected_cost_basis ?? 'amount',
    cost: p.projected_cost != null ? String(p.projected_cost) : '',
    percent: p.projected_cost_percent != null ? String(p.projected_cost_percent) : '',
    dirty: false,
  }
}

export function AdminMenuCostingTab() {
  const qc = useQueryClient()
  const { currencyCode } = useCurrency()
  const [guideExpanded, setGuideExpanded] = useState(readCostingGuideExpanded)
  const [search, setSearch] = useState('')
  const [categoryFilter, setCategoryFilter] = useState<string>('all')
  const [coverageFilter, setCoverageFilter] = useState<'all' | 'with' | 'without'>('all')
  const [drafts, setDrafts] = useState<Record<string, RowDraft>>({})
  const [bulkOpen, setBulkOpen] = useState(false)
  const [bulkPercent, setBulkPercent] = useState('')
  const [bulkBasis, setBulkBasis] = useState<ProjectedCostBasis>('percent')
  const [bulkAmount, setBulkAmount] = useState('')

  const toggleGuide = useCallback(() => {
    setGuideExpanded((prev) => {
      const next = !prev
      try {
        localStorage.setItem(COSTING_GUIDE_EXPANDED_KEY, String(next))
      } catch {
        /* ignore quota / private mode */
      }
      return next
    })
  }, [])

  const productsQuery = useQuery({
    // High per_page so the costing tab shows the entire menu at once. Costing
    // setup is naturally a "scan and edit" workflow so paging would be more
    // friction than benefit; backend caps per_page at 100, so we accept that
    // ceiling for v1 and add a pagination strip later if any single venue
    // outgrows it.
    queryKey: ['admin-products', 'costing'],
    queryFn: () => apiClient.getProducts({ page: 1, per_page: 100 }).then((res) => res.data ?? []),
  })

  const categoriesQuery = useQuery({
    queryKey: ['admin-categories', 'costing'],
    queryFn: () => apiClient.getCategories().then((res) => res.data ?? []),
  })

  const products = useMemo(() => productsQuery.data ?? [], [productsQuery.data])
  const categories = useMemo(() => categoriesQuery.data ?? [], [categoriesQuery.data])

  // Coverage stats: how many products have a cost set, both raw and as a
  // sales-weighted figure pulled from the existing reports overview (last 30
  // days). Side-by-side, these answer "have I set up enough costs?" honestly.
  const itemCoverage = useMemo(() => {
    const total = products.length
    const withCost = products.filter(productHasCost).length
    return { total, withCost, pct: total === 0 ? 0 : (withCost / total) * 100 }
  }, [products])

  const overviewQuery = useQuery({
    queryKey: ['reports-overview', 'costing-tab', '30d'],
    queryFn: async () => {
      const today = new Date()
      const start = new Date(today)
      start.setDate(start.getDate() - 29)
      const fmt = (d: Date) => d.toISOString().slice(0, 10)
      return apiClient.getReportsOverview(fmt(start), fmt(today)).then((res) => res.data)
    },
  })

  const salesCoveragePct = overviewQuery.data?.projected_coverage_pct?.current ?? 0

  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase()
    return products.filter((p) => {
      if (s && !p.name.toLowerCase().includes(s)) return false
      if (categoryFilter !== 'all' && (p.category_id ?? '') !== categoryFilter) return false
      if (coverageFilter === 'with' && !productHasCost(p)) return false
      if (coverageFilter === 'without' && productHasCost(p)) return false
      return true
    })
  }, [products, search, categoryFilter, coverageFilter])

  const getDraft = useCallback(
    (p: Product): RowDraft => drafts[p.id] ?? deriveDraft(p),
    [drafts],
  )

  const setDraft = useCallback((id: string, patch: Partial<RowDraft>) => {
    setDrafts((prev) => {
      const current = prev[id] ?? { basis: 'amount' as ProjectedCostBasis, cost: '', percent: '', dirty: false }
      return { ...prev, [id]: { ...current, ...patch, dirty: true } }
    })
  }, [])

  const updateMutation = useMutation({
    mutationFn: async (vars: { id: string; basis: ProjectedCostBasis; cost: number | null; percent: number | null }) => {
      return apiClient.updateProduct(vars.id, {
        projected_cost_basis: vars.basis,
        projected_cost: vars.cost,
        projected_cost_percent: vars.percent,
      })
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['admin-products'] })
      qc.invalidateQueries({ queryKey: ['products'] })
      qc.invalidateQueries({ queryKey: ['reports-overview'] })
      setDrafts((prev) => {
        const next = { ...prev }
        delete next[vars.id]
        return next
      })
    },
    onError: (error) => {
      toastHelpers.apiError('Update cost', error)
    },
  })

  const saveRow = useCallback(
    (p: Product) => {
      const draft = drafts[p.id]
      if (!draft) return
      let cost: number | null = null
      let percent: number | null = null
      if (draft.basis === 'amount') {
        const n = Number(draft.cost)
        if (draft.cost.trim() === '' || Number.isNaN(n) || n < 0) {
          toastHelpers.error('Invalid cost', 'Enter a number 0 or greater')
          return
        }
        cost = n
      } else {
        const n = Number(draft.percent)
        if (draft.percent.trim() === '' || Number.isNaN(n) || n < 0 || n > 100) {
          toastHelpers.error('Invalid percent', 'Enter a number between 0 and 100')
          return
        }
        percent = n
      }
      updateMutation.mutate({ id: p.id, basis: draft.basis, cost, percent })
    },
    [drafts, updateMutation],
  )

  const clearRow = useCallback(
    (p: Product) => {
      updateMutation.mutate({ id: p.id, basis: 'amount', cost: null, percent: null })
    },
    [updateMutation],
  )

  const bulkApply = useCallback(async () => {
    const target = filtered.filter((p) => !productHasCost(p))
    if (target.length === 0) {
      toastHelpers.error('Nothing to apply', 'No filtered items are missing a cost')
      return
    }
    const cost = bulkBasis === 'amount' ? Number(bulkAmount) : null
    const percent = bulkBasis === 'percent' ? Number(bulkPercent) : null
    if (bulkBasis === 'amount' && (Number.isNaN(cost!) || cost! < 0)) {
      toastHelpers.error('Invalid cost', 'Enter a number 0 or greater')
      return
    }
    if (bulkBasis === 'percent' && (Number.isNaN(percent!) || percent! < 0 || percent! > 100)) {
      toastHelpers.error('Invalid percent', 'Enter a number between 0 and 100')
      return
    }
    // Issue requests serially so we never overwhelm the API. Costing rolls in
    // small batches in practice (a single venue's menu).
    let ok = 0
    for (const p of target) {
      try {
        await apiClient.updateProduct(p.id, {
          projected_cost_basis: bulkBasis,
          projected_cost: cost,
          projected_cost_percent: percent,
        })
        ok++
      } catch (e) {
        toastHelpers.apiError(`Bulk: ${p.name}`, e)
      }
    }
    qc.invalidateQueries({ queryKey: ['admin-products'] })
    qc.invalidateQueries({ queryKey: ['products'] })
    qc.invalidateQueries({ queryKey: ['reports-overview'] })
    toastHelpers.success('Bulk costs applied', `${ok} of ${target.length} updated`)
    setBulkOpen(false)
    setBulkAmount('')
    setBulkPercent('')
  }, [filtered, bulkBasis, bulkAmount, bulkPercent, qc])

  // Both modes share the same prefix-symbol layout (e.g. "Rs. [____]" or
  // "%   [____]") and the same input width so rows line up vertically when
  // the column mixes amount and percent entries — important for scanning.
  const renderCostInput = (p: Product) => {
    const draft = getDraft(p)
    const isPercent = draft.basis === 'percent'
    const symbol = isPercent ? '%' : currencyInputPrefix(currencyCode)
    return (
      <div className="flex items-center justify-center gap-1.5">
        <span className="w-8 text-right text-xs text-muted-foreground">{symbol}</span>
        <Input
          type="number"
          min={0}
          max={isPercent ? 100 : undefined}
          step={0.01}
          inputMode="decimal"
          placeholder={isPercent ? '35' : '0.00'}
          value={isPercent ? draft.percent : draft.cost}
          onChange={(e) =>
            setDraft(p.id, isPercent ? { percent: e.target.value } : { cost: e.target.value })
          }
          className="h-8 w-24 text-right"
        />
      </div>
    )
  }

  const computedPreview = (p: Product) => {
    const draft = getDraft(p)
    const price = p.price ?? 0
    let cost: number | null = null
    if (draft.basis === 'percent') {
      const pct = Number(draft.percent)
      cost = !Number.isFinite(pct) ? null : (price * pct) / 100
    } else {
      const n = Number(draft.cost)
      cost = !Number.isFinite(n) ? null : n
    }
    if (cost == null || Number.isNaN(cost)) return null
    const margin = price - cost
    const marginPct = price > 0 ? (margin / price) * 100 : 0
    return { cost, margin, marginPct }
  }

  return (
    <div className="space-y-6">
      {/* Walkthrough panel — elevated to a "hero" treatment that mirrors the
          Reports → Hours tab (gradient wash + soft glow + eyebrow + icon chip)
          so the admin surfaces feel like one family. Themed emerald/teal —
          margins read as money — to stay distinct from the amber Hours hero.
          Still localStorage-backed so power users collapse it once for good. */}
      <Card className="overflow-hidden duration-500 animate-in fade-in slide-in-from-bottom-2">
        <div className="relative bg-gradient-to-br from-amber-50 via-orange-50/50 to-transparent dark:from-amber-950/30 dark:via-orange-950/15 dark:to-transparent">
          <div
            aria-hidden
            className="pointer-events-none absolute -right-12 -top-16 h-44 w-44 rounded-full bg-amber-400/20 blur-3xl dark:bg-amber-500/10"
          />
          <CardContent className="relative space-y-4 p-5 sm:p-6">
            <button
              type="button"
              id="costing-guide-toggle"
              aria-expanded={guideExpanded}
              aria-controls="costing-guide-panel"
              onClick={toggleGuide}
              className="group flex w-full items-center justify-between gap-3 rounded-md text-left"
            >
              <div className="flex items-center gap-3">
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-amber-500/15 text-amber-600 ring-1 ring-inset ring-amber-500/20 dark:text-amber-400">
                  <Lightbulb className="h-5 w-5" aria-hidden />
                </span>
                <div>
                  <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-amber-700 dark:text-amber-400">
                    Menu costing
                  </div>
                  <h2 id="costing-guide-title" className="text-lg font-semibold leading-tight">
                    Costing &amp; margins
                  </h2>
                  {!guideExpanded && (
                    <p className="mt-0.5 text-sm text-muted-foreground">
                      Click to expand (remembered on this device).
                    </p>
                  )}
                </div>
              </div>
              <ChevronDown
                className={cn(
                  'h-5 w-5 shrink-0 text-muted-foreground transition-transform duration-200 group-hover:text-foreground',
                  guideExpanded && 'rotate-180',
                )}
                aria-hidden
              />
            </button>
            {guideExpanded && (
              <div
                id="costing-guide-panel"
                className="space-y-4 duration-300 animate-in fade-in slide-in-from-top-1"
                role="region"
                aria-labelledby="costing-guide-title"
              >
                <p className="max-w-2xl text-sm leading-relaxed text-muted-foreground">
                  Record what each item costs you to make. Reports then show your projected{' '}
                  <span className="font-medium text-foreground">profit and margin</span> on every
                  sale — an estimate shown next to your real Net Profit, never a replacement for it.
                </p>

                {/* How it works — three steps tied to the actual controls in the
                    table below, so the panel teaches the workflow instead of
                    describing the feature in the abstract. */}
                <div>
                  <div className="mb-2.5 text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
                    How it works
                  </div>
                  <ol className="grid gap-3 sm:grid-cols-3">
                    {[
                      {
                        n: 1,
                        title: 'Find an item',
                        body: 'Search by name, or set the filter to “Missing cost only”.',
                      },
                      {
                        n: 2,
                        title: 'Choose Rs or %',
                        body: 'Tap the Rs / % toggle in the Mode column to pick how you’ll enter the cost.',
                      },
                      {
                        n: 3,
                        title: 'Type, then Save',
                        body: 'Projected profit and margin update live as you type. Hit Save on the row.',
                      },
                    ].map((step) => (
                      <li
                        key={step.n}
                        className="rounded-xl border bg-card/60 p-3.5 shadow-sm backdrop-blur-sm"
                      >
                        <div className="flex items-center gap-2.5">
                          <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-amber-500/15 text-xs font-semibold text-amber-700 ring-1 ring-inset ring-amber-500/20 dark:text-amber-400">
                            {step.n}
                          </span>
                          <span className="text-sm font-medium">{step.title}</span>
                        </div>
                        <p className="mt-2 text-xs leading-relaxed text-muted-foreground">
                          {step.body}
                        </p>
                      </li>
                    ))}
                  </ol>
                  <p className="mt-2.5 text-xs text-muted-foreground">
                    In a hurry? <span className="font-medium text-foreground">Bulk apply</span> sets one
                    default across every filtered item that&rsquo;s still missing a cost.
                  </p>
                </div>

                {/* Two cost modes, each with a worked example that mirrors the
                    Projected profit / Margin % columns — turning the abstract
                    "amount vs percent" choice into concrete money. */}
                <div>
                  <div className="mb-2.5 text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
                    Two ways to enter a cost
                  </div>
                  <div className="grid gap-3 sm:grid-cols-2">
                    <div className="rounded-xl border bg-card/60 p-3.5 shadow-sm backdrop-blur-sm transition-colors hover:border-emerald-500/40 hover:bg-card">
                      <div className="flex items-center gap-2.5">
                        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-emerald-500/15 text-emerald-600 dark:text-emerald-400">
                          <Wallet className="h-4 w-4" aria-hidden />
                        </span>
                        <div>
                          <div className="text-sm font-medium">Fixed amount (Rs)</div>
                          <div className="text-xs text-muted-foreground">The exact cost to make it</div>
                        </div>
                      </div>
                      <p className="mt-2.5 rounded-lg bg-muted/60 px-2.5 py-2 text-xs leading-relaxed tabular-nums">
                        Burger sells for Rs 1,200, costs Rs 700 to make &rarr;{' '}
                        <span className="font-semibold text-emerald-600 dark:text-emerald-400">
                          Rs 500 profit (42%)
                        </span>
                      </p>
                      <p className="mt-2 text-xs leading-relaxed text-muted-foreground">
                        Best when the cost barely moves.
                      </p>
                    </div>
                    <div className="rounded-xl border bg-card/60 p-3.5 shadow-sm backdrop-blur-sm transition-colors hover:border-blue-500/40 hover:bg-card">
                      <div className="flex items-center gap-2.5">
                        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-blue-500/15 text-blue-600 dark:text-blue-400">
                          <Percent className="h-4 w-4" aria-hidden />
                        </span>
                        <div>
                          <div className="text-sm font-medium">% of price</div>
                          <div className="text-xs text-muted-foreground">A share of the sale price</div>
                        </div>
                      </div>
                      <p className="mt-2.5 rounded-lg bg-muted/60 px-2.5 py-2 text-xs leading-relaxed tabular-nums">
                        35% of a Rs 1,200 sale = Rs 420 cost &rarr;{' '}
                        <span className="font-semibold text-blue-600 dark:text-blue-400">
                          Rs 780 profit (65%)
                        </span>
                      </p>
                      <p className="mt-2 text-xs leading-relaxed text-muted-foreground">
                        Cost follows the price &mdash; change the price and it re-figures itself.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </div>
      </Card>

      {/* Coverage banner */}
      <Card>
        <CardContent className="flex flex-wrap items-center justify-between gap-4 p-4 sm:p-6">
          <div className="space-y-1">
            <p className="text-sm font-medium">
              Costs added for{' '}
              <span className="text-foreground">{itemCoverage.withCost}</span> of{' '}
              <span className="text-foreground">{itemCoverage.total}</span> items
              <span className="text-muted-foreground">
                {' '}
                ({itemCoverage.pct.toFixed(0)}%)
              </span>
            </p>
            <p className="text-xs text-muted-foreground">
              Last 30 days: <span className="text-foreground">{salesCoveragePct.toFixed(0)}%</span> of sales came from items that have a cost.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => setBulkOpen(true)}>
              Bulk apply…
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Filters */}
      <Card>
        <CardContent className="flex flex-wrap items-center gap-3 p-4">
          <Input
            placeholder="Search products by name…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="max-w-sm flex-1"
          />
          <Select value={categoryFilter} onValueChange={setCategoryFilter}>
            <SelectTrigger className="w-[200px]">
              <SelectValue placeholder="All categories" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All categories</SelectItem>
              {categories.map((c: Category) => (
                <SelectItem key={c.id} value={String(c.id)}>
                  {c.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={coverageFilter} onValueChange={(v) => setCoverageFilter(v as typeof coverageFilter)}>
            <SelectTrigger className="w-[180px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All items</SelectItem>
              <SelectItem value="without">Missing cost only</SelectItem>
              <SelectItem value="with">With cost set</SelectItem>
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      {/* Editable table */}
      <Card>
        <CardContent className="p-0">
          {productsQuery.isLoading ? (
            <div className="flex items-center justify-center py-16 text-sm text-muted-foreground">
              <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading menu…
            </div>
          ) : filtered.length === 0 ? (
            <div className="py-16 text-center text-sm text-muted-foreground">
              No items match the current filters.
            </div>
          ) : (
            <div className="overflow-x-auto [-webkit-overflow-scrolling:touch]">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Item</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead className="text-right">Price</TableHead>
                  <TableHead>Mode</TableHead>
                  <TableHead className="text-center">Cost / Percentage</TableHead>
                  <TableHead className="text-right">Projected profit</TableHead>
                  <TableHead className="text-right">Margin %</TableHead>
                  <TableHead className="w-[160px] text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((p) => {
                  const draft = getDraft(p)
                  const preview = computedPreview(p)
                  const hasCost = productHasCost(p)
                  const isPending = updateMutation.isPending && updateMutation.variables?.id === p.id
                  return (
                    <TableRow key={p.id}>
                      <TableCell className="align-middle">
                        <div className="flex min-w-0 items-center gap-3">
                          <div className="flex-shrink-0">
                            {p.image_url ? (
                              <img
                                src={p.image_url}
                                alt={p.name}
                                className="h-9 w-9 rounded-md border bg-muted object-cover"
                              />
                            ) : (
                              <ProductImagePlaceholder className="h-9 w-9" iconClassName="h-4 w-4" />
                            )}
                          </div>
                          <div className="flex min-w-0 flex-wrap items-center gap-2">
                            <span className="font-medium">{p.name}</span>
                            {!hasCost && !draft.dirty && (
                              <Badge variant="outline" className="border-amber-300 text-amber-700 dark:border-amber-700 dark:text-amber-300">
                                No cost set
                              </Badge>
                            )}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {p.category?.name ?? '—'}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {formatMoney(p.price, currencyCode)}
                      </TableCell>
                      <TableCell>
                        <div className="inline-flex rounded-md border bg-background p-0.5">
                          <button
                            type="button"
                            onClick={() => setDraft(p.id, { basis: 'amount' })}
                            className={cn(
                              'rounded px-2 py-0.5 text-xs font-medium transition-colors',
                              draft.basis !== 'percent'
                                ? 'bg-primary text-primary-foreground'
                                : 'text-muted-foreground hover:bg-muted',
                            )}
                          >
                            {currencyInputPrefix(currencyCode)}
                          </button>
                          <button
                            type="button"
                            onClick={() => setDraft(p.id, { basis: 'percent' })}
                            className={cn(
                              'rounded px-2 py-0.5 text-xs font-medium transition-colors',
                              draft.basis === 'percent'
                                ? 'bg-primary text-primary-foreground'
                                : 'text-muted-foreground hover:bg-muted',
                            )}
                          >
                            %
                          </button>
                        </div>
                      </TableCell>
                      <TableCell className="text-center">{renderCostInput(p)}</TableCell>
                      <TableCell className="text-right tabular-nums">
                        {preview ? (
                          <span className={cn(preview.margin < 0 && 'text-destructive')}>
                            {formatMoney(preview.margin, currencyCode)}
                          </span>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {preview ? (
                          <span className={cn(preview.margin < 0 && 'text-destructive')}>
                            {preview.marginPct.toFixed(1)}%
                          </span>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          {draft.dirty && (
                            <Button
                              size="sm"
                              className="h-7"
                              onClick={() => saveRow(p)}
                              disabled={isPending}
                            >
                              {isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Save'}
                            </Button>
                          )}
                          {hasCost && !draft.dirty && (
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-7 text-xs text-destructive hover:text-destructive"
                              onClick={() => clearRow(p)}
                              disabled={isPending}
                            >
                              Clear
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Bulk apply dialog */}
      <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Bulk apply cost</DialogTitle>
            <DialogDescription className="text-xs">
              Apply a default cost to every item in the current filter that doesn&rsquo;t already have one
              ({filtered.filter((p) => !productHasCost(p)).length} items). Existing costs are left
              untouched.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <Info className="h-4 w-4 text-muted-foreground" aria-hidden />
              <p className="text-xs text-muted-foreground">
                Use this for a quick rough-in. You can refine individual items afterwards.
              </p>
            </div>
            <div className="inline-flex rounded-md border bg-background p-0.5">
              <button
                type="button"
                onClick={() => setBulkBasis('amount')}
                className={cn(
                  'rounded px-3 py-1.5 text-xs font-medium transition-colors',
                  bulkBasis === 'amount'
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:bg-muted',
                )}
              >
                Amount ({currencyInputPrefix(currencyCode)})
              </button>
              <button
                type="button"
                onClick={() => setBulkBasis('percent')}
                className={cn(
                  'rounded px-3 py-1.5 text-xs font-medium transition-colors',
                  bulkBasis === 'percent'
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:bg-muted',
                )}
              >
                % of price
              </button>
            </div>
            {bulkBasis === 'amount' ? (
              <Input
                type="number"
                min={0}
                step={0.01}
                inputMode="decimal"
                placeholder="0.00"
                value={bulkAmount}
                onChange={(e) => setBulkAmount(e.target.value)}
              />
            ) : (
              <Input
                type="number"
                min={0}
                max={100}
                step={0.01}
                inputMode="decimal"
                placeholder="35"
                value={bulkPercent}
                onChange={(e) => setBulkPercent(e.target.value)}
              />
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkOpen(false)}>
              Cancel
            </Button>
            <Button onClick={bulkApply}>Apply to filtered items</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
